import React, { useState, useRef, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { MessageCircle, Send, X, Trash2, Copy, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { chatService } from '../../services/chatService'
import { authAPI } from '../../services/api'
import { useLocalMode } from '../../contexts/LocalModeContext'

/**
 * ChatWithNoteModal Component
 * 
 * A large, responsive modal for chatting with AI about note content.
 * Features glassmorphism design, conversation history, and context awareness.
 */
const ChatWithNoteModal = ({ 
  isOpen, 
  onClose, 
  note,
  noteTitle = ''
}) => {
  // State management
  const [conversation, setConversation] = useState([])
  const [currentInput, setCurrentInput] = useState('')
  const [isAiTyping, setIsAiTyping] = useState(false)
  const [aiConfig, setAiConfig] = useState(null)
  
  // Refs
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  
  // Local mode context
  const { isLocalMode } = useLocalMode()

  // Load AI configuration when modal opens
  useEffect(() => {
    if (isOpen && !isLocalMode) {
      loadAIConfiguration()
    }
  }, [isOpen, isLocalMode])

  const loadAIConfiguration = async () => {
    try {
      const config = await authAPI.getAIConfiguration()
      setAiConfig(config)
      console.log('Loaded AI configuration for chat:', config)
    } catch (error) {
      console.error('Failed to load AI configuration:', error)
      toast.error('Failed to load AI configuration')
    }
  }

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [conversation, isAiTyping])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Load conversation history from localStorage
  useEffect(() => {
    if (isOpen && note?.noteId) {
      const savedConversation = chatService.getConversationHistory(note.noteId)
      if (savedConversation.length > 0) {
        setConversation(savedConversation)
      }
    }
  }, [isOpen, note?.noteId])


  const handleSendMessage = async () => {
    // Check if in local mode and show message (same as RichTextEditor)
    if (isLocalMode) {
      toast.error('AI features only available once you Sign up', {
        duration: 4000,
        style: {
          background: 'rgba(239, 68, 68, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }
      })
      return
    }

    if (!currentInput.trim() || !aiConfig || isAiTyping) return

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: currentInput.trim(),
      timestamp: new Date().toISOString()
    }

    const newConversation = [...conversation, userMessage]
    setConversation(newConversation)
    setCurrentInput('')
    setIsAiTyping(true)

    try {
      // Build context with note content and conversation history
      const context = chatService.buildChatContext(
        note, 
        newConversation, 
        true // Always include note context
      )

      // Call AI service
      const aiResponse = await chatService.sendMessage(
        context,
        aiConfig,
        newConversation
      )

      const aiMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date().toISOString(),
        modelUsed: aiConfig.aiModel
      }

      const finalConversation = [...newConversation, aiMessage]
      setConversation(finalConversation)

      // Save to localStorage
      chatService.saveConversationHistory(note.noteId, finalConversation)

    } catch (error) {
      console.error('Chat error:', error)
      toast.error(`Chat failed: ${error.message}`)
      
      // Remove user message on error
      setConversation(conversation)
    } finally {
      setIsAiTyping(false)
    }
  }

  const clearConversation = () => {
    setConversation([])
    chatService.clearConversationHistory(note.noteId)
    toast.success('Conversation cleared')
  }

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content)
    toast.success('Message copied to clipboard')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[60] p-2 sm:p-4">
      <div className="bg-white/40 dark:bg-gray-800/40 backdrop-blur-[40px] rounded-2xl shadow-2xl border border-white/30 dark:border-gray-700/30 max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col mx-2 sm:mx-4">
        
        {/* Header - Enhanced with larger spacing and better proportions */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Chat with Note
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-96">
                {noteTitle || 'Untitled Note'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {conversation.length > 0 && (
              <button
                onClick={clearConversation}
                className="p-2 rounded-xl text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
                title="Clear conversation"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Chat Configuration - Simplified */}
        <div className="px-8 py-4 border-b border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
          <div className="flex items-center gap-2">
            {/* AI Model Information */}
            <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
              AI Model:
            </label>
            <span className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600">
              {aiConfig ? aiConfig.aiModel : (isLocalMode ? 'Sign up to use AI' : 'Loading...')}
            </span>
          </div>
        </div>

        {/* Chat Messages - Enhanced with larger area and better spacing */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gradient-to-br from-gray-50/20 to-gray-100/20 dark:from-gray-900/20 dark:to-gray-800/20 min-h-[400px]">
          {conversation.length === 0 ? (
            <div className="text-center py-16">
              <MessageCircle className="w-20 h-20 text-gray-300 dark:text-gray-600 mx-auto mb-6" />
              <p className="text-gray-500 dark:text-gray-400 text-xl">
                Start a conversation about this note
              </p>
              <p className="text-gray-400 dark:text-gray-500 text-base mt-3">
                Ask questions, get summaries, or explore the content with AI
              </p>
            </div>
          ) : (
            conversation.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-6 py-4 ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white shadow-lg'
                      : 'bg-white/70 dark:bg-gray-800/70 text-gray-900 dark:text-gray-100 border border-gray-200/30 dark:border-gray-700/30 shadow-md'
                  }`}
                >
                  <div className="prose prose-base max-w-none dark:prose-invert">
                    {message.content}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-current/20">
                    <span className="text-xs opacity-70">
                      {new Date(message.timestamp).toLocaleTimeString()}
                      {message.modelUsed && ` • ${message.modelUsed}`}
                    </span>
                    <button
                      onClick={() => copyMessage(message.content)}
                      className="text-xs opacity-50 hover:opacity-100 transition-opacity p-1"
                      title="Copy message"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* AI Typing Indicator */}
          {isAiTyping && (
            <div className="flex justify-start">
              <div className="bg-white/70 dark:bg-gray-800/70 text-gray-900 dark:text-gray-100 border border-gray-200/30 dark:border-gray-700/30 rounded-2xl px-6 py-4 shadow-md">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-sm text-gray-500">AI is thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - Enhanced with larger input field and better spacing */}
        <div className="px-6 py-6 border-t border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <textarea
                ref={inputRef}
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  isLocalMode 
                    ? "Sign up to chat with AI..." 
                    : "Ask a detailed question about this note..."
                }
                className="w-full px-5 py-4 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/90 dark:bg-gray-700/90 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none min-h-[80px] max-h-40 text-base"
                rows={3}
                disabled={isAiTyping || !aiConfig || isLocalMode}
              />
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={handleSendMessage}
                disabled={!currentInput.trim() || isAiTyping || !aiConfig || isLocalMode}
                className="h-[96px] w-[80px] rounded-xl bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center border border-transparent"
                title="Send message"
              >
                {isAiTyping ? (
                  <RefreshCw className="w-6 h-6 animate-spin" />
                ) : (
                  <Send className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
          
          {/* Privacy Notice */}
          <div className="mt-3 flex items-center justify-center">
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
              Note data will be shared with AI model
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default ChatWithNoteModal
